import {
  H3,
  H4,
  mediaHelper,
  paletteHelper,
  radiusHelper,
  spacingHelper,
  typographyHelper,
} from '@property.com.au/ui-kit';
import { DataGetter, trackEvent } from '@rea-argonaut/analytics';
import styled from 'styled-components';
import { PartnerBannerVariant } from './GovernmentDataPartnerBannerWithVariant';
import { ObservedElement } from '../../../../../app-kit/components/ObservedElement/ObservedElement';
import { WebLink } from '../../../../../app-kit/components/WebLink/WebLink';
import { getGovernmentDataPartnerBannerTracking } from '../../helpers/getGovernmentDataPartnerBannerTracking';
import tioSvg from '../../../../../assets/tio.svg';
import allianzSvg from '../../../../../assets/allianz.svg';

const Card = styled.div`
  background-color: ${paletteHelper('backgroundInformation')};

  border-radius: ${radiusHelper('large')};
  display: flex;
  flex-direction: column;
  padding: ${spacingHelper('large')};
  margin-bottom: ${spacingHelper('small')};
  gap: ${spacingHelper('twoExtraSmall')};

  ${mediaHelper('mobile')} {
    padding: ${spacingHelper('medium')};
  }
`;

const Description = styled(H4)`
  ${mediaHelper('tabletMin')} {
    max-width: calc(${spacingHelper('extraLarge')} * 16);
  }
`;

const ButtonContainer = styled.div`
  margin-top: ${spacingHelper('medium')};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  row-gap: 1.25rem;

  ${mediaHelper('tabletMin')} {
    flex-direction: row;
  }
`;

const StyledWebLink = styled(WebLink)`
  width: auto;

  /* Allianz brand colour */
  background-color: rgb(0 55 129 / 100%);
  &:hover,
  &:focus,
  &:focus-visible,
  &:active {
    background-color: rgb(0 55 129 / 80%);
  }

  ${mediaHelper('tabletMin')} {
    order: -1;
  }
`;

const Logo = styled.img`
  width: 9rem;
  height: 2.5rem;
`;

const NTLogo = styled.img`
  width: 4.5rem;
  height: 2.5rem;
`;

const Disclaimer = styled.div`
  ${typographyHelper('caption')};
  color: ${paletteHelper('textSecondary')};
`;

const trackGovernmentDataPartnerCtaImpression = ({
  state,
  partnerBannerVariant,
}: {
  state: string | null;
  partnerBannerVariant: PartnerBannerVariant;
}) => {
  const event: DataGetter = () => ({
    schema: 'iglu:au.com.property/pca_display_ads/jsonschema/1-0-0',
    data: {
      event_name: 'pca_display_ads',
      user_action: 'impression',
      type: 'banner',
      name: 'allianz_banner',
      context: [
        {
          key: 'state',
          value: state ? state : '',
        },
        {
          key: 'partner_banner_variant',
          value: partnerBannerVariant,
        },
      ],
    },
  });

  trackEvent(event);
};

export const GovernmentDataPartnerBanner = ({
  title,
  description,
  disclaimerText,
  ctaText,
  ctaLink,
  experimentId,
  experimentVariation,
  isPropertyNorthernTerritory,
  onCtaClick,
  state,
  partnerBannerVariant,
}: {
  title: string;
  description: string;
  disclaimerText: string;
  ctaText: string;
  ctaLink: string;
  experimentId: string;
  experimentVariation: string;
  isPropertyNorthernTerritory: boolean;
  onCtaClick: () => void;
  state: string | null;
  partnerBannerVariant: PartnerBannerVariant;
}) => {
  if (title === '' || ctaLink === '') return null;

  const tracking = getGovernmentDataPartnerBannerTracking(
    experimentId,
    experimentVariation,
    ctaLink,
    state,
    partnerBannerVariant,
  );

  return (
    <ObservedElement
      onFirstIntersect={() =>
        trackGovernmentDataPartnerCtaImpression({
          state,
          partnerBannerVariant,
        })
      }
    >
      <Card data-testid="government-data-partner-banner">
        <H3 variant="body01Bold">{title}</H3>
        <Description variant="subtitle02">{description}</Description>
        <ButtonContainer>
          {isPropertyNorthernTerritory ? (
            <NTLogo loading="lazy" alt="TIO Logo" src={tioSvg} />
          ) : (
            <Logo loading="lazy" alt="Allianz Logo" src={allianzSvg} />
          )}
          <StyledWebLink
            sizeVariant="medium"
            variant="button-primary"
            isExternal
            title={ctaText ?? title}
            url={ctaLink}
            tracking={tracking}
            onClick={onCtaClick}
          />
        </ButtonContainer>
      </Card>
      <Disclaimer>{disclaimerText}</Disclaimer>
    </ObservedElement>
  );
};
